import React, { useState } from 'react';
import { Phone, Mail, MapPin, Instagram, Upload, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { businessInfo } from '../mock';
import { toast } from 'sonner';

const Footer = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    description: '',
    image: null
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.size <= 10 * 1024 * 1024) {
      setFormData(prev => ({ ...prev, image: file }));
      toast.success(`${file.name} pronto para enviar`);
    } else {
      toast.error("Arquivo muito grande. Por favor, envie uma imagem menor que 10MB");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('description', formData.description);
      
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }
      
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/estimate`, {
        method: 'POST',
        body: formDataToSend
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setIsSubmitted(true);
        setFormData({ name: '', phone: '', description: '', image: null });
      } else {
        throw new Error(data.detail || 'Failed to send request');
      }
    } catch (error) {
      toast.error('Error sending request', {
        description: error.message || "Failed to send estimate request. Please try again."
      });
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      {/* Contact Form Section */}
      <div className="py-16 px-4 bg-gray-800">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-8">Request Your Free Estimate</h2>
          <Card className="p-8 bg-white">
            {isSubmitted ? (
              // Success Message
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-12 h-12 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Message Sent Successfully!</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">
                  Thank you for contacting us. We will respond as soon as possible.
                </p>
                <Button 
                  onClick={() => setIsSubmitted(false)}
                  className="bg-[#FFC222] hover:bg-[#E6AE12] text-black font-bold px-6 py-3 rounded-lg"
                >
                  Send Another Request
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="h-12 text-base rounded-lg"
                />
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="h-12 text-base rounded-lg"
                />
              </div>
              <Textarea
                name="description"
                placeholder="Describe your project"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows={4}
                className="text-base rounded-lg"
              />
              <label className="flex items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#FFC222] transition-all bg-gray-50">
                <div className="text-center">
                  <Upload className="mx-auto mb-2 text-gray-500" size={24} />
                  <span className="text-sm text-gray-600">
                    {formData.image ? formData.image.name : 'Upload Photo (Optional, Max 10MB)'}
                  </span>
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
              <Button 
                type="submit" 
                className="w-full bg-[#FFC222] hover:bg-[#E6AE12] text-black font-bold text-lg py-6 rounded-xl shadow-lg transition-all"
              >
                Send Request
              </Button>
            </form>
            )}
          </Card>
        </div>
      </div>

      {/* Footer Info */}
      <div className="py-12 px-4">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_florida-remodel/artifacts/6wge7g1p_Brown%20Modern%20Simple%20Construction%20Logo%20-%205.PNG" 
                alt="SAGU Construction Logo" 
                className="h-12 w-auto object-contain"
              />
              <h3 className="text-2xl font-bold">
                SAGU <span className="text-[#FFC222]">CONSTRUCTION</span>
              </h3>
            </div>
            <p className="text-gray-300 mb-4">Licensed & Insured Florida LLC</p>
            <p className="text-gray-400 text-sm">Serving Central Florida with quality remodeling services since 2015</p>
          </div>
          <div>
            <h4 className="text-xl font-semibold mb-4 text-[#FFC222]">Contact Us</h4>
            <div className="space-y-3">
              <a href={`tel:${businessInfo.phone}`} className="flex items-center gap-2 text-gray-300 hover:text-[#FFC222] transition-colors">
                <Phone size={18} />
                <span>{businessInfo.phone}</span>
              </a>
              <a href={`mailto:${businessInfo.email}`} className="flex items-center gap-2 text-gray-300 hover:text-[#FFC222] transition-colors">
                <Mail size={18} />
                <span>{businessInfo.email}</span>
              </a>
              <div className="flex items-center gap-2 text-gray-300">
                <MapPin size={18} />
                <span>{businessInfo.location}</span>
              </div>
            </div>
          </div>
          <div>
            <h4 className="text-xl font-semibold mb-4 text-[#FFC222]">Business Hours</h4>
            <p className="text-gray-300">{businessInfo.hours}</p>
            <p className="text-gray-400 text-sm mt-2">Saturday & Sunday: Closed</p>
            <div className="mt-4">
              <a 
                href={`https://instagram.com/${businessInfo.instagram.replace('@', '')}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-gray-300 hover:text-[#FFC222] transition-colors"
              >
                <Instagram size={20} />
                <span>{businessInfo.instagram}</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-gray-700 py-6 px-4">
        <p className="text-center text-gray-400 text-sm">
          © {new Date().getFullYear()} SAGU CONSTRUCTION LLC — Licensed & Insured • Orlando, FL
        </p>
      </div>
    </footer>
  );
};

export default Footer;
